/**
 * Voortgang / contactmomenten — rebuilt from scratch.
 *
 * KEY PRINCIPLES (per user spec):
 *  - dealId is read STRICTLY from `useParams()` (the URL path) — never hardcoded
 *  - POST goes to `/api/sales/deals/${dealId}/moments`
 *  - GET  comes from `/api/sales/deals/${dealId}/moments`
 *  - After a successful POST we IMMEDIATELY re-fetch the list and update state
 *  - We use native `fetch()` with explicit error handling and verbose console logs
 *  - Timeline shows ONLY the moments returned by the GET for this dealId
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatEuro, STAGES } from "@/sales/lib/api";
import { useAuth } from "@/sales/auth/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Activity,
  ArrowLeft,
  Save,
  Trash2,
  Users,
  Phone,
  Video,
  Mail,
  MessageCircle,
  Globe,
  Plus,
  Building2,
  Calendar as CalendarIcon,
  Pencil,
  Eye,
  CheckCircle2,
  AlertCircle,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Token-aware fetch wrapper. Injects the Bearer JWT on every request, and on
 * 401 it wipes the stored session and bounces the user back to /login. This is
 * the single chokepoint for all backend I/O in this module — never call the
 * bare `fetch()` here.
 */
function authedFetch(input, init = {}) {
  const token = localStorage.getItem("riskai_token");
  const headers = new Headers(init.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  return fetch(input, { ...init, headers }).then((res) => {
    if (res.status === 401) {
      localStorage.removeItem("riskai_token");
      localStorage.removeItem("riskai_user");
      toast.error("Sessie verlopen", {
        description: "U wordt doorgestuurd naar de inlogpagina.",
      });
      setTimeout(() => {
        if (window.location.pathname !== "/login") {
          window.location.assign("/sales/login");
        }
      }, 1500);
    }
    return res;
  });
}

const SUBCATEGORIES = [
  { id: "Fysiek", icon: Users, color: "#A78BFA" },
  { id: "Telefonisch", icon: Phone, color: "#38BDF8" },
  { id: "Video contact", icon: Video, color: "#F472B6" },
  { id: "E-mail", icon: Mail, color: "#FBBF24" },
  { id: "WhatsApp", icon: MessageCircle, color: "#34D399" },
  { id: "Online", icon: Globe, color: "#C0C0C0" },
];
const findSub = (id) => SUBCATEGORIES.find((s) => s.id === id) || SUBCATEGORIES[0];

const DEFAULT_OMSCHRIJVING = "_Doel:_ \n\n_Afspraak:_ \n\n_Vervolg:_ ";
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  id: null,
  datum: todayISO(),
  subcategorie: "Fysiek",
  contactpersoon: "",
  omschrijving: DEFAULT_OMSCHRIJVING,
});

export default function ContactMoments() {
  const { dealId } = useParams(); // ← strictly from URL
  const navigate = useNavigate();
  const { isGast } = useAuth();

  const [deal, setDeal] = useState(null);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [mode, setMode] = useState(isGast ? "view" : "new"); // 'new' | 'edit' | 'view'
  const [banner, setBanner] = useState(null);
  // Moment slated for deletion from the timeline (drives the modal)
  const [momentToDelete, setMomentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isEditing = mode === "edit";
  const isReadOnly = mode === "view" || isGast;

  const showBanner = (type, text) => {
    setBanner({ type, text });
    if (type === "success") {
      setTimeout(() => setBanner(null), 4500);
    }
  };

  // --- Fetch moments for THIS deal only ---
  const refreshMoments = useCallback(async () => {
    const url = `${BACKEND}/api/sales/deals/${dealId}/moments`;
    console.log("[Voortgang] GET", url);
    const res = await authedFetch(url);
    if (!res.ok) {
      throw new Error(`GET failed ${res.status}`);
    }
    const data = await res.json();
    console.log(
      "[Voortgang] received",
      data.length,
      "moments for deal_id",
      dealId
    );
    setMoments(data);
    return data;
  }, [dealId]);

  // --- Fetch deal context + moments on mount / when dealId changes ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [dealRes] = await Promise.all([
          authedFetch(`${BACKEND}/api/sales/deals/${dealId}`),
          refreshMoments(),
        ]);
        if (cancelled) return;
        if (dealRes.ok) {
          setDeal(await dealRes.json());
        } else {
          toast.error("Kon traject niet laden");
        }
      } catch (err) {
        console.error("[Voortgang] initial load failed", err);
        toast.error("Kon traject niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, refreshMoments]);

  // --- Save handler (POST for new, PUT for edit) ---
  const handleSave = async () => {
    if (isReadOnly) return;
    const payload = {
      datum: form.datum,
      subcategorie: form.subcategorie,
      contactpersoon: form.contactpersoon || "",
      omschrijving: form.omschrijving || "",
    };
    console.log("SAVING:", dealId, payload, "mode=", mode, "formId=", form.id);

    if (!form.datum) {
      showBanner("error", "Datum is verplicht");
      return;
    }

    const url = isEditing
      ? `${BACKEND}/api/sales/moments/${form.id}`
      : `${BACKEND}/api/sales/deals/${dealId}/moments`;
    const method = isEditing ? "PUT" : "POST";

    // Safely read a fetch Response body exactly once, returning either the
    // parsed JSON or `null` on any failure (network / parse / already-read).
    const readBodySafely = async (resp) => {
      try {
        return await resp.clone().json();
      } catch {
        try {
          return { detail: await resp.clone().text() };
        } catch {
          return null;
        }
      }
    };

    try {
      const res = await authedFetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
        mode: "cors",
      });

      console.log("SAVE response status:", res.status, res.statusText);

      if (!res.ok) {
        const body = await readBodySafely(res);
        const errMsg =
          (body && (body.detail || body.message)) ||
          `${res.status} ${res.statusText}`;
        console.error("SAVE FAILED:", res.status, body);
        showBanner("error", `❌ Fout (${res.status}): ${errMsg}`);
        toast.error("Opslaan mislukt", { description: String(errMsg) });
        return;
      }

      const savedDoc = await readBodySafely(res);
      console.log("SAVED OK ->", savedDoc);

      // IMMEDIATELY re-fetch the canonical list for this deal
      const refreshed = await refreshMoments();
      console.log(
        "FETCHED:",
        refreshed.length,
        "moments for deal",
        dealId,
        "— includes saved id?",
        savedDoc && refreshed.some((m) => m.id === savedDoc.id)
      );

      const successText = isEditing
        ? `✅ Contactmoment bijgewerkt · ${payload.subcategorie} · ${payload.datum}`
        : `✅ Contactmoment opgeslagen · ${payload.subcategorie} · ${payload.datum}`;
      showBanner("success", successText);
      toast.success(successText);

      setForm(emptyForm());
      setMode("new");
    } catch (err) {
      console.error("SAVE FAILED (network):", err);
      showBanner("error", `❌ Netwerkfout: ${err.message || err}`);
      toast.error("Opslaan mislukt", { description: err.message || String(err) });
    }
  };

  // --- Delete handler ---
  const handleDelete = async () => {
    if (!isEditing || !form.id) return;
    if (!window.confirm("Weet je zeker dat je dit contactmoment wilt verwijderen?"))
      return;
    try {
      const res = await authedFetch(`${BACKEND}/api/sales/moments/${form.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const txt = await res.text();
        showBanner("error", `❌ Verwijderen mislukt: ${txt}`);
        toast.error("Verwijderen mislukt", { description: txt });
        return;
      }
      await refreshMoments();
      setForm(emptyForm());
      setMode("new");
      showBanner("success", "✅ Contactmoment verwijderd");
      toast.success("Contactmoment verwijderd");
    } catch (err) {
      showBanner("error", `❌ Netwerkfout: ${err.message}`);
    }
  };

  // --- Load a moment into the form (edit or view mode) ---
  const loadMoment = (m, nextMode) => {
    setForm({
      id: m.id,
      datum: m.datum || "",
      subcategorie: m.subcategorie || "Fysiek",
      contactpersoon: typeof m.contactpersoon === "string" ? m.contactpersoon : "",
      omschrijving: typeof m.omschrijving === "string" ? m.omschrijving : "",
    });
    setMode(nextMode);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const startEdit = (m) => loadMoment(m, "edit");
  const startView = (m) => loadMoment(m, "view");
  const startNew = () => {
    setForm(emptyForm());
    setMode("new");
  };

  // --- Delete a timeline entry (triggered from per-entry button) ---
  const requestDelete = (m) => setMomentToDelete(m);
  const cancelDelete = () => setMomentToDelete(null);
  const confirmDelete = async () => {
    if (!momentToDelete) return;
    setDeleting(true);
    try {
      const res = await authedFetch(`${BACKEND}/api/sales/moments/${momentToDelete.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.clone().json().catch(async () => ({
          detail: await res.clone().text().catch(() => "Onbekende fout"),
        }));
        const msg = body?.detail || `${res.status} ${res.statusText}`;
        showBanner("error", `❌ Verwijderen mislukt: ${msg}`);
        toast.error("Verwijderen mislukt", { description: String(msg) });
        return;
      }
      // If the moment being deleted is currently loaded in the form, reset.
      if (form.id === momentToDelete.id) {
        setForm(emptyForm());
        setMode("new");
      }
      await refreshMoments();
      showBanner("success", "✅ Contactmoment verwijderd");
      toast.success("Contactmoment verwijderd");
    } catch (err) {
      console.error("DELETE failed", err);
      showBanner("error", `❌ Netwerkfout: ${err.message || err}`);
      toast.error("Verwijderen mislukt", { description: err.message || String(err) });
    } finally {
      setDeleting(false);
      setMomentToDelete(null);
    }
  };

  const stageLabel = deal
    ? STAGES.find((s) => s.id === deal.stage)?.label || deal.stage
    : "";

  // ===================== RENDER =====================
  return (
    <main className="w-full max-w-[1280px] mx-auto px-6 md:px-10 pb-16 pt-2">
      {/* Traject context strip */}
      <div className="mb-5">
        {loading ? (
          <Skeleton className="h-16 bg-[#142539]/60 rounded-lg" />
        ) : deal ? (
          <div className="bg-[#142539]/80 border border-[rgba(192,192,192,0.15)] rounded-lg p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded bg-[#1C3A5A] border border-[rgba(192,192,192,0.25)] flex items-center justify-center flex-shrink-0">
              <Building2 size={16} className="text-[#C0C0C0]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#64748B]">
                Traject
              </p>
              <h2 className="font-serif-display text-lg md:text-xl text-[#F8FAFC] truncate">
                {deal.municipality}
              </h2>
              <p
                data-testid="traject-deal-id"
                className="text-[10px] font-mono text-[#475569] truncate"
                title={deal.id}
              >
                ID · {deal.id}
              </p>
            </div>
            <div className="hidden md:flex items-center gap-5 text-xs">
              <Field label="Waarde" value={formatEuro(deal.value)} />
              <Field label="Fase" value={stageLabel} />
              <Field label="Contactmomenten" value={`${moments.length}`} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-rose-400">Traject niet gevonden.</p>
        )}
      </div>

      {/* Form panel */}
      <section
        data-testid="contact-moments-form"
        data-mode={mode}
        data-dealid={dealId}
        className={`rounded-lg overflow-hidden border mb-8 transition-colors ${
          isReadOnly
            ? "border-[#475569] bg-[#0F172A]/60"
            : "border-[#334155] bg-[#0F172A]"
        }`}
      >
        {banner && (
          <div
            data-testid={`cm-banner-${banner.type}`}
            className={`px-5 py-3 flex items-center gap-3 text-sm font-medium ${
              banner.type === "success"
                ? "bg-[#16A34A] text-white"
                : "bg-[#DC2626] text-white"
            }`}
          >
            {banner.type === "success" ? (
              <CheckCircle2 size={18} className="flex-shrink-0" />
            ) : (
              <AlertCircle size={18} className="flex-shrink-0" />
            )}
            <span className="flex-1">{banner.text}</span>
            <button
              type="button"
              onClick={() => setBanner(null)}
              className="opacity-70 hover:opacity-100"
              aria-label="Sluit melding"
            >
              <X size={16} />
            </button>
          </div>
        )}

        <header
          className={`px-5 py-3 flex items-center gap-3 ${
            isReadOnly ? "bg-[#374151]" : "bg-[#475569]"
          }`}
        >
          <Activity size={16} className="text-white" />
          <h3 className="text-sm font-semibold tracking-wide text-white">
            Voortgang / contactmomenten
          </h3>
          {isEditing && (
            <span
              data-testid="cm-badge-edit"
              className="ml-auto text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-200 px-2 py-0.5 rounded"
            >
              Bewerken
            </span>
          )}
          {isReadOnly && (
            <span
              data-testid="cm-badge-readonly"
              className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#1E293B] text-[#94A3B8] border border-[#475569] px-2 py-0.5 rounded"
            >
              <Eye size={10} /> Alleen lezen
            </span>
          )}
        </header>

        <div className="bg-[#1E293B] px-5 py-3 border-b border-[#334155] flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="cm-back-btn"
            onClick={() => navigate("/sales")}
            className="inline-flex items-center gap-1.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded px-4 py-2 transition-colors"
          >
            <ArrowLeft size={15} /> Terug
          </button>

          {!isReadOnly && (
            <button
              type="button"
              data-testid="cm-save-btn"
              onClick={handleSave}
              className={`inline-flex items-center gap-1.5 text-white text-sm font-medium rounded px-4 py-2 transition-colors ${
                isEditing
                  ? "bg-[#2563EB] hover:bg-[#1D4ED8]"
                  : "bg-[#16A34A] hover:bg-[#15803D]"
              }`}
            >
              <Save size={15} /> {isEditing ? "Bijwerken" : "Opslaan"}
            </button>
          )}

          {!isReadOnly && (
            <button
              type="button"
              data-testid="cm-delete-btn"
              onClick={handleDelete}
              disabled={!isEditing}
              className={`inline-flex items-center gap-1.5 text-sm font-medium rounded px-4 py-2 transition-colors ${
                isEditing
                  ? "bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                  : "bg-[#7F1D1D]/30 text-rose-300/40 cursor-not-allowed"
              }`}
            >
              <Trash2 size={15} /> Verwijder
            </button>
          )}

          {(isEditing || isReadOnly) && !isGast && (
            <button
              type="button"
              data-testid="cm-new-btn"
              onClick={startNew}
              className="ml-auto inline-flex items-center gap-1.5 border border-[#16A34A] text-[#86EFAC] hover:bg-[#16A34A]/15 text-sm rounded px-3 py-2 font-medium"
            >
              <Plus size={14} /> Nieuw contactmoment
            </button>
          )}
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4">
          <FieldGroup label="Datum" className="md:col-span-3">
            <input
              type="date"
              data-testid="cm-input-datum"
              value={form.datum}
              onChange={(e) => setForm({ ...form, datum: e.target.value })}
              disabled={isReadOnly}
              className={`w-full bg-[#0B1521] border rounded px-3 py-2 text-sm text-[#F8FAFC] focus:outline-none ${
                isReadOnly
                  ? "border-[#1E293B] opacity-80 cursor-not-allowed"
                  : "border-[#334155] focus:border-[#94A3B8]"
              }`}
            />
          </FieldGroup>

          <FieldGroup label="Subcategorie" className="md:col-span-4">
            <Select
              value={form.subcategorie}
              onValueChange={(v) => setForm({ ...form, subcategorie: v })}
              disabled={isReadOnly}
            >
              <SelectTrigger
                data-testid="cm-input-subcategorie"
                disabled={isReadOnly}
                className={`bg-[#0B1521] text-[#F8FAFC] ${
                  isReadOnly
                    ? "border-[#1E293B] opacity-80 cursor-not-allowed"
                    : "border-[#334155]"
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#142539] border-[#334155] text-[#F8FAFC]">
                {SUBCATEGORIES.map((s) => {
                  const Icon = s.icon;
                  return (
                    <SelectItem key={s.id} value={s.id} className="focus:bg-[#1C3A5A]">
                      <span className="inline-flex items-center gap-2">
                        <Icon size={14} style={{ color: s.color }} />
                        {s.id}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </FieldGroup>

          <FieldGroup label="Contactpersoon" className="md:col-span-5">
            <input
              type="text"
              data-testid="cm-input-contactpersoon"
              value={form.contactpersoon}
              onChange={(e) => setForm({ ...form, contactpersoon: e.target.value })}
              disabled={isReadOnly}
              placeholder="Naam contactpersoon…"
              className={`w-full bg-[#0B1521] border rounded px-3 py-2 text-sm text-[#F8FAFC] placeholder:text-[#475569] focus:outline-none ${
                isReadOnly
                  ? "border-[#1E293B] opacity-80 cursor-not-allowed"
                  : "border-[#334155] focus:border-[#94A3B8]"
              }`}
            />
          </FieldGroup>

          <FieldGroup label="Omschrijving" className="md:col-span-12">
            <textarea
              key={form.id || "new"}
              data-testid="cm-input-omschrijving"
              value={form.omschrijving}
              onChange={(e) => setForm({ ...form, omschrijving: e.target.value })}
              readOnly={isReadOnly}
              rows={9}
              style={{ minHeight: "220px" }}
              className={`w-full bg-[#0B1521] border rounded px-3 py-2.5 text-sm text-[#F8FAFC] focus:outline-none font-mono leading-relaxed ${
                isReadOnly
                  ? "border-[#1E293B] opacity-90 cursor-default resize-none"
                  : "border-[#334155] focus:border-[#94A3B8] resize-y"
              }`}
            />
            {!isReadOnly && (
              <p className="text-[10px] text-[#64748B] mt-1.5 uppercase tracking-wider">
                Tip · Headings <span className="text-[#94A3B8]">_Doel:_</span>{" "}
                <span className="text-[#94A3B8]">_Afspraak:_</span>{" "}
                <span className="text-[#94A3B8]">_Vervolg:_</span> worden onderstreept
                getoond in de tijdlijn.
              </p>
            )}
          </FieldGroup>
        </div>
      </section>

      {/* Timeline */}
      <section data-testid="contact-moments-timeline">
        <div className="bg-[#475569] rounded-t-lg px-5 py-3 flex items-center gap-3">
          <CalendarIcon size={16} className="text-white" />
          <h3 className="text-sm font-semibold tracking-wide text-white">
            Tijdlijn · alle contactmomenten
          </h3>
          <span className="ml-auto text-[11px] text-white/75">
            {moments.length} {moments.length === 1 ? "moment" : "momenten"}
          </span>
        </div>
        <div className="border border-t-0 border-[#334155] rounded-b-lg bg-[#0F172A] p-6">
          {loading ? (
            <Skeleton className="h-40 bg-[#142539]/60" />
          ) : moments.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-8">
              Nog geen contactmomenten voor dit traject. Vul het formulier hierboven in
              en klik op <span className="text-emerald-400">Opslaan</span>.
            </p>
          ) : (
            <Timeline
              moments={moments}
              onEdit={startEdit}
              onView={startView}
              onDelete={requestDelete}
              activeId={form.id}
              activeMode={mode}
              readOnly={isGast}
            />
          )}
        </div>
      </section>

      {/* Delete confirmation modal */}
      <AlertDialog
        open={!!momentToDelete}
        onOpenChange={(open) => {
          if (!open) cancelDelete();
        }}
      >
        <AlertDialogContent
          data-testid="cm-delete-dialog"
          className="bg-[#0F172A] border border-[#334155] text-[#F8FAFC]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif-display text-lg text-[#F8FAFC]">
              Contactmoment verwijderen?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#94A3B8]">
              {momentToDelete ? (
                <>
                  Je staat op het punt om het contactmoment van{" "}
                  <span className="text-[#E2E8F0] font-medium">
                    {formatDateNL(momentToDelete.datum)}
                  </span>{" "}
                  ({momentToDelete.subcategorie}) definitief te verwijderen. Deze actie
                  kan niet ongedaan worden gemaakt.
                </>
              ) : (
                "Deze actie kan niet ongedaan worden gemaakt."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cm-delete-cancel"
              disabled={deleting}
              className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] hover:bg-[#334155] hover:text-[#F8FAFC]"
            >
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cm-delete-confirm"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            >
              {deleting ? "Verwijderen…" : "Ja, verwijder"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.18em] text-[#64748B]">{label}</p>
      <p className="text-sm text-[#E2E8F0] font-medium">{value}</p>
    </div>
  );
}

function FieldGroup({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] mb-1.5 font-semibold">
        {label}
      </label>
      {children}
    </div>
  );
}

function Timeline({ moments, onEdit, onView, onDelete, activeId, activeMode, readOnly = false }) {
  return (
    <ol className="relative space-y-6">
      <div
        className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-[#334155] to-transparent"
        aria-hidden
      />
      {moments.map((m) => (
        <TimelineEntry
          key={m.id}
          moment={m}
          onEdit={() => onEdit(m)}
          onView={() => onView(m)}
          onDelete={() => onDelete(m)}
          isActive={activeId === m.id}
          activeMode={activeMode}
          readOnly={readOnly}
        />
      ))}
    </ol>
  );
}

function TimelineEntry({ moment, onEdit, onView, onDelete, isActive, activeMode, readOnly = false }) {
  const sub = findSub(moment.subcategorie);
  const Icon = sub.icon;
  const isEditingThis = isActive && activeMode === "edit";
  const isViewingThis = isActive && activeMode === "view";
  return (
    <li data-testid={`timeline-entry-${moment.id}`} className="relative pl-14">
      <div
        className="absolute left-0 top-1 w-10 h-10 rounded-full flex items-center justify-center border-2 z-10"
        style={{
          background: "#0F172A",
          borderColor: sub.color,
          boxShadow: `0 0 0 4px #0F172A, 0 0 12px ${sub.color}40`,
        }}
      >
        <Icon size={16} style={{ color: sub.color }} />
      </div>
      <div
        className={`rounded-lg border p-4 transition-colors ${
          isEditingThis
            ? "border-amber-400/60 bg-amber-500/5"
            : isViewingThis
            ? "border-[#94A3B8]/60 bg-[#1E293B]/50"
            : "border-[#334155] bg-[#142539]/60 hover:border-[#475569]"
        }`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wider text-[#94A3B8] flex items-center gap-2">
              <span style={{ color: sub.color }} className="font-semibold">
                {sub.id}
              </span>
              <span className="text-[#475569]">·</span>
              <span className="text-[#E2E8F0]">{formatDateNL(moment.datum)}</span>
            </p>
            {moment.contactpersoon && (
              <p
                data-testid={`timeline-contactpersoon-${moment.id}`}
                className="text-[11px] text-[#94A3B8] mt-1 inline-flex items-center gap-1.5"
              >
                <Users size={11} className="text-[#64748B]" />
                <span className="text-[#E2E8F0]">{moment.contactpersoon}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              type="button"
              data-testid={`view-moment-${moment.id}`}
              onClick={onView}
              title="Bekijken (alleen lezen)"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#94A3B8] hover:text-[#E2E8F0] border border-[#475569] hover:border-[#94A3B8] rounded px-2 py-1 bg-[#1E293B]/40"
            >
              <Eye size={11} /> Bekijken
            </button>
            {!readOnly && (
              <button
                type="button"
                data-testid={`edit-moment-${moment.id}`}
                onClick={onEdit}
                title="Bewerken"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#94A3B8] hover:text-[#C0C0C0] border border-[#334155] hover:border-[#475569] rounded px-2 py-1"
              >
                <Pencil size={11} /> Bewerken
              </button>
            )}
            {!readOnly && (
              <button
                type="button"
                data-testid={`delete-moment-${moment.id}`}
                onClick={() => onDelete && onDelete()}
                title="Verwijderen"
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#F87171] hover:text-white hover:bg-[#DC2626] border border-[#DC2626]/60 hover:border-[#DC2626] rounded px-2 py-1 bg-[#DC2626]/10"
              >
                <Trash2 size={11} /> Verwijderen
              </button>
            )}
          </div>
        </div>
        <FormattedDescription text={moment.omschrijving} />
      </div>
    </li>
  );
}

function FormattedDescription({ text }) {
  if (!text || !text.trim()) {
    return <p className="text-xs italic text-[#64748B]">Geen omschrijving</p>;
  }
  const parts = text.split(/(_[^_\n]+_)/g);
  return (
    <div className="text-sm text-[#E2E8F0] leading-relaxed whitespace-pre-wrap break-words">
      {parts.map((p, i) => {
        if (/^_[^_\n]+_$/.test(p)) {
          return (
            <span key={i} className="font-semibold text-[#C0C0C0] underline">
              {p.replace(/^_|_$/g, "")}
            </span>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </div>
  );
}

function formatDateNL(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
